import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { FirestoreService } from "../../../../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../../../api/firebase/references";
import { createRuleListInlineHandlers } from "../../../../../components/modals/shared/ruleFormConstants";
import { usePaRequestsActionFieldOptions } from "../../../../../components/modals/shared/usePaRequestsActionFieldOptions";
import { useCmmInputUniqueValuesContext } from "../../../../../context/cmmInputUniqueValuesContext";
import { mergePaRequestsMetadataIntoConditionConfig } from "../../../../../data-model/pharmacyOrderUpdatesPaRequestsMetadata";
import { RootState } from "../../../../../redux/store/store";
import type { CallingRuleTrigger } from "../../callingConfiguration/callingRuleUtils";
import {
  buildFirstStpReviewConditionConfigFromCmmColumns,
  createEmptyFirstStpReviewRule,
  DEFAULT_FIRST_STP_DOC_TRIGGERS,
  DEFAULT_FIRST_STP_REVIEW_DOC_ID,
  FIRST_STP_REVIEW_CONDITION_FIELD_OPTIONS,
  FIRST_STP_REVIEW_CONDITION_TITLE,
  getDefaultFirstStpReviewRules,
  mapFirstStpReviewDocToRuleStates,
  mapFirstStpReviewRuleStatesToFirestorePayload,
  resolveFirstStpDocTriggers,
  type FirstStpReviewRuleDoc,
  type FirstStpReviewRuleState,
} from "./firstStpReviewRuleUtils";

const cloneRules = (
  rules: FirstStpReviewRuleState[],
): FirstStpReviewRuleState[] =>
  JSON.parse(JSON.stringify(rules)) as FirstStpReviewRuleState[];

const cloneTriggers = (triggers: CallingRuleTrigger[]): CallingRuleTrigger[] =>
  JSON.parse(JSON.stringify(triggers)) as CallingRuleTrigger[];

/** Manages editable first STP review rules at pharmacy_order_updates/rules/first_stp_review. */
export function useFirstStpReviewRules(
  firestoreDoc: FirstStpReviewRuleDoc | null,
) {
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const appliedDocRef = useRef(false);
  const [rules, setRules] = useState<FirstStpReviewRuleState[]>(
    getDefaultFirstStpReviewRules(),
  );
  const [triggers, setTriggers] = useState<CallingRuleTrigger[]>(
    cloneTriggers(DEFAULT_FIRST_STP_DOC_TRIGGERS),
  );
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const lastSavedRulesRef = useRef<FirstStpReviewRuleState[]>(
    getDefaultFirstStpReviewRules(),
  );
  const lastSavedTriggersRef = useRef<CallingRuleTrigger[]>(
    cloneTriggers(DEFAULT_FIRST_STP_DOC_TRIGGERS),
  );
  const [lastSavedMeta, setLastSavedMeta] = useState<{
    updated_at?: string;
    updated_by?: string;
  } | null>(null);
  const [ruleLoadedVersion, setRuleLoadedVersion] = useState(0);
  const existingDocRef = useRef<FirstStpReviewRuleDoc | null>(null);

  useEffect(() => {
    if (firestoreDoc == null || appliedDocRef.current) return;
    appliedDocRef.current = true;
    existingDocRef.current = firestoreDoc;
    const mapped = mapFirstStpReviewDocToRuleStates(firestoreDoc);
    const seed = mapped.length > 0 ? mapped : getDefaultFirstStpReviewRules();
    const docTriggers = resolveFirstStpDocTriggers(firestoreDoc);
    setRules(seed);
    setTriggers(docTriggers);
    lastSavedRulesRef.current = cloneRules(seed);
    lastSavedTriggersRef.current = cloneTriggers(docTriggers);
    setLastSavedMeta({
      updated_at: firestoreDoc.updated_at,
      updated_by: firestoreDoc.updated_by,
    });
    setRuleLoadedVersion((v) => v + 1);
  }, [firestoreDoc]);

  const markDirty = () => setIsDirty(true);

  const handleAddNewRuleClick = () => {
    markDirty();
    setRules((prev) => [...prev, createEmptyFirstStpReviewRule(prev.length)]);
  };

  const {
    handleRuleLabelChange,
    handleRuleSubtextChange,
    handleRuleActiveToggle,
    handleRuleConditionChange,
    handleRuleActionFieldChange,
    handleRuleActionTypeChange,
    handleRuleActionStringValueChange,
    handleRuleActionMultiValueChange,
    handleRuleAddAction,
    handleRuleRemoveAction,
  } = createRuleListInlineHandlers(setRules, markDirty);

  const handleDeleteRule = (ruleId: string) => {
    markDirty();
    setRules((prev) => prev.filter((r) => r.id !== ruleId));
  };

  const handleTriggerTypeChange = (index: number, newType: string) => {
    markDirty();
    setTriggers((prev) =>
      prev.map((t, i) => {
        if (i !== index) return t;
        if (newType === "cron") {
          const schedule =
            (t as { schedule?: string }).schedule ?? "0 12 * * *";
          return { type: "cron", schedule };
        }
        return { type: newType };
      }),
    );
  };

  const handleTriggerScheduleChange = (index: number, schedule: string) => {
    markDirty();
    setTriggers((prev) =>
      prev.map((t, i) =>
        i === index && t.type === "cron" ? { type: "cron", schedule } : t,
      ),
    );
  };

  const handleAddTrigger = () => {
    markDirty();
    setTriggers((prev) => [...prev, { type: "api" }]);
  };

  const handleRemoveTrigger = (index: number) => {
    markDirty();
    setTriggers((prev) => prev.filter((_, i) => i !== index));
  };

  const { cmmInputUniqueValues } = useCmmInputUniqueValuesContext();
  const {
    metadata: paRequestsMetadata,
    fieldOptions: paRequestsFieldOptions,
    valueConditionConfig,
  } = usePaRequestsActionFieldOptions();

  const conditionConfig = useMemo(() => {
    const pageConfig = buildFirstStpReviewConditionConfigFromCmmColumns(
      cmmInputUniqueValues,
      FIRST_STP_REVIEW_CONDITION_FIELD_OPTIONS,
    );
    const mergedFromMetadata = mergePaRequestsMetadataIntoConditionConfig(
      pageConfig,
      paRequestsMetadata,
      FIRST_STP_REVIEW_CONDITION_FIELD_OPTIONS,
    );
    return mergedFromMetadata ?? pageConfig;
  }, [cmmInputUniqueValues, paRequestsMetadata]);

  const actionFieldOptions = useMemo(
    () =>
      paRequestsFieldOptions.length > 0
        ? paRequestsFieldOptions
        : conditionConfig.fields.map((f) => ({
            value: f.value,
            label: f.label,
          })),
    [paRequestsFieldOptions, conditionConfig],
  );

  const actionValueConfig = useMemo(
    () =>
      valueConditionConfig.fields.length > 0
        ? valueConditionConfig
        : conditionConfig,
    [valueConditionConfig, conditionConfig],
  );

  const configKey = useMemo(
    () =>
      conditionConfig.fields.length > 0
        ? conditionConfig.fields
            .map((f) => f.value)
            .sort()
            .join(",")
        : "empty",
    [conditionConfig],
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = mapFirstStpReviewRuleStatesToFirestorePayload(
        rules,
        triggers,
        existingDocRef.current,
        user?.email ?? undefined,
      );
      await FirestoreService.setDocument(
        FirestoreCollectionReference.pharmacyOrderUpdatesRules(),
        DEFAULT_FIRST_STP_REVIEW_DOC_ID,
        payload,
      );
      existingDocRef.current = {
        ...(existingDocRef.current ?? {
          id: DEFAULT_FIRST_STP_REVIEW_DOC_ID,
          rules: [],
        }),
        ...payload,
      };
      lastSavedRulesRef.current = cloneRules(rules);
      lastSavedTriggersRef.current = cloneTriggers(triggers);
      setIsDirty(false);
      setLastSavedMeta({
        updated_at: payload.updated_at,
        updated_by: payload.updated_by ?? user?.email ?? undefined,
      });
      toast.success("Changes saved");
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : "Failed to save. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    if (!window.confirm("Are you sure? All unsaved changes will be lost."))
      return;
    setRules(cloneRules(lastSavedRulesRef.current));
    setTriggers(cloneTriggers(lastSavedTriggersRef.current));
    setIsDirty(false);
    setRuleLoadedVersion((v) => v + 1);
  };

  return {
    rules,
    triggers,
    conditionConfig,
    actionFieldOptions,
    actionValueConfig,
    configKey,
    conditionTitle: FIRST_STP_REVIEW_CONDITION_TITLE,
    isDirty,
    saving,
    ruleLoadedVersion,
    handleAddNewRuleClick,
    handleRuleLabelChange,
    handleRuleSubtextChange,
    handleRuleActiveToggle,
    handleRuleConditionChange,
    handleRuleActionFieldChange,
    handleRuleActionTypeChange,
    handleRuleActionStringValueChange,
    handleRuleActionMultiValueChange,
    handleRuleAddAction,
    handleRuleRemoveAction,
    handleDeleteRule,
    handleTriggerTypeChange,
    handleTriggerScheduleChange,
    handleAddTrigger,
    handleRemoveTrigger,
    handleSave,
    handleDiscard,
    lastSavedMeta,
  };
}
